import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mic } from "lucide-react";
import { api } from "../../api";
import { useProfile } from "../../layout/AppShell";
import { fadeUp, stagger } from "../../lib/motion";
import { cn } from "../../lib/format";

// Slider state — 0..1 floats. Five dimensions taken straight from the
// design mock; defaults match the design's mid/high values.
type Sliders = {
  warmth: number;     // brisk → warm
  terseness: number;  // rambly → clipped
  curiosity: number;  // answers → asks
  formality: number;  // du · slang → Sie · prim
  humor: number;      // serious → playful
};

const DEFAULTS: Sliders = {
  warmth: 0.7,
  terseness: 0.4,
  curiosity: 0.6,
  formality: 0.3,
  humor: 0.5,
};

const STORAGE_KEY = "ilmli.custom_persona";

function loadStored(): Sliders {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw) as Partial<Sliders>;
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

// Derive a German preview line from slider positions. Per the user's
// note: "no name. ilmli stays ilmli." — the speaker is always ilmli, the
// only thing changing is voice.
function previewLine(s: Sliders): string {
  // Pick clauses per slider; threshold 0.5.
  const open =
    s.warmth > 0.7
      ? "Komm her, erzähl mir alles."
      : s.warmth > 0.4
        ? "Hallo. Wie geht's heute?"
        : "Anfangen.";
  const middle =
    s.terseness > 0.7
      ? "Schnell. Drei Sätze."
      : s.terseness > 0.4
        ? "Wann hast du Zeit?"
        : "Ich höre dir zu — alles, von Anfang bis Ende.";
  const close =
    s.curiosity > 0.6
      ? "Und warum?"
      : s.formality > 0.6
        ? "Würden Sie das bitte erläutern?"
        : s.humor > 0.6
          ? "Na, kleine Katastrophe?"
          : "Bitte.";
  return `${open} ${middle} ${close}`.trim();
}

function previewBehavior(s: Sliders): { register: string; length: string; followups: string; emoji: string } {
  return {
    register: s.formality > 0.5 ? "Sie" : "du",
    length: s.terseness > 0.7 ? "4–8 words" : s.terseness > 0.4 ? "12–18 words" : "25–40 words",
    followups:
      s.curiosity > 0.7
        ? "80% of turns"
        : s.curiosity > 0.4
          ? "60% of turns"
          : "30% of turns",
    emoji: s.humor > 0.7 ? "frequent" : s.humor > 0.4 ? "rare" : "never",
  };
}

export default function CustomPersona() {
  const nav = useNavigate();
  const { profile, setProfile } = useProfile();
  // Hydrate from the user's saved blob first, falling back to localStorage
  // (for guests who staged sliders before signup) and then to defaults.
  const [s, setS] = useState<Sliders>(() => {
    const saved = profile.custom_persona;
    if (saved && Object.keys(saved).length) {
      return { ...DEFAULTS, ...saved } as Sliders;
    }
    return loadStored();
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Stage every change locally so the preview updates instantly. Don't
  // round-trip to the API on every drag — that'd hammer it. Backend save
  // happens on explicit "Save & activate".
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch { /* noop */ }
  }, [s]);

  const sample = useMemo(() => previewLine(s), [s]);
  const behavior = useMemo(() => previewBehavior(s), [s]);

  function update<K extends keyof Sliders>(k: K, v: number) {
    setS((prev) => ({ ...prev, [k]: v }));
  }

  async function onSave() {
    if (saving) return;
    setSaving(true);
    setErr(null);
    try {
      const updated = await api.updateMe({ custom_persona: s });
      setProfile(updated);
      setSavedAt(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "couldn't save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <motion.div initial="hidden" animate="show" variants={stagger(0.05)}>
      <motion.div variants={fadeUp}>
        <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-faint">
          {(profile.languages?.length ?? 1) > 1 ? "polyglot · custom personality" : "custom personality"}
        </div>
        <h1 className="mt-3 font-serif text-3xl md:text-4xl leading-tight">
          Design your <span className="italic text-terracotta">tutor.</span>
        </h1>
        <p className="mt-2 max-w-xl font-serif italic text-ink-soft text-base">
          Five sliders. A sample line — updates as you slide. No name to pick;
          ilmli stays ilmli, only the voice changes.
        </p>
      </motion.div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Sliders */}
        <motion.div variants={fadeUp} className="rounded-md border border-[rgba(42,36,26,0.18)] bg-paper p-6 md:p-7">
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-faint mb-5">
            voice
          </div>
          <Slider label="warmth"    lo="brisk"      hi="warm"       value={s.warmth}    onChange={(v) => update("warmth", v)} />
          <Slider label="terseness" lo="rambly"     hi="clipped"    value={s.terseness} onChange={(v) => update("terseness", v)} />
          <Slider label="curiosity" lo="answers"    hi="asks"       value={s.curiosity} onChange={(v) => update("curiosity", v)} />
          <Slider label="formality" lo="du · slang" hi="Sie · prim" value={s.formality} onChange={(v) => update("formality", v)} />
          <Slider label="humor"     lo="serious"    hi="playful"    value={s.humor}     onChange={(v) => update("humor", v)} last />
        </motion.div>

        {/* Sample + behavior preview */}
        <motion.div variants={fadeUp} className="space-y-4">
          <div className="rounded-md bg-ink-card text-paper p-5">
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-paper/45 mb-3">
              sample first line
            </div>
            <p className="font-serif italic text-[18px] leading-snug text-paper">
              {sample}
            </p>
            <button
              type="button"
              disabled
              title="Voice preview wires to the TTS pipeline — coming soon"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-paper/25 px-2.5 py-1 font-mono text-[11px] text-paper/70 cursor-not-allowed"
            >
              <Mic size={11} />
              hear it
              <span className="ml-1.5 text-paper/45">soon</span>
            </button>
          </div>
          <div className="rounded-md border border-[rgba(42,36,26,0.18)] bg-paper p-5">
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-faint mb-3">
              behavior preview
            </div>
            <ul className="space-y-1.5 font-serif text-[15px]">
              <li>· will use <strong className="not-italic font-medium">{behavior.register}</strong></li>
              <li>· average reply length: <strong className="not-italic font-medium">{behavior.length}</strong></li>
              <li>· asks follow-up questions <strong className="not-italic font-medium">{behavior.followups}</strong></li>
              <li>· emoji: <strong className="not-italic font-medium">{behavior.emoji}</strong></li>
            </ul>
          </div>
        </motion.div>
      </div>

      <motion.div variants={fadeUp} className="mt-8 flex items-center gap-4 flex-wrap">
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded-md bg-terracotta text-paper px-5 py-2.5 text-[14px] font-medium hover:bg-terracotta-soft disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save & activate"}
        </button>
        <button
          type="button"
          onClick={() => nav("/app/personas")}
          className="text-[13px] text-ink-soft underline decoration-ink-faint/40 hover:decoration-terracotta hover:text-terracotta"
        >
          cancel
        </button>
        {savedAt && (
          <span className="font-mono italic text-[12px] text-[#386641]">
            saved at {savedAt}
          </span>
        )}
        {err && (
          <span className="font-mono italic text-[12px] text-terracotta">{err}</span>
        )}
      </motion.div>
    </motion.div>
  );
}

function Slider({
  label,
  lo,
  hi,
  value,
  onChange,
  last,
}: {
  label: string;
  lo: string;
  hi: string;
  value: number;
  onChange: (v: number) => void;
  last?: boolean;
}) {
  return (
    <div className={cn(last ? "" : "mb-5")}>
      <div className="flex items-baseline justify-between gap-3 mb-1.5">
        <span className="font-mono text-[11px] text-ink-soft">{lo}</span>
        <span className="font-mono text-[11px] text-ink uppercase tracking-wide">{label}</span>
        <span className="font-mono text-[11px] text-ink-soft">{hi}</span>
      </div>
      <input
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full"
        style={{ accentColor: "var(--terracotta)" }}
      />
    </div>
  );
}
